// bottomActions.js
import React, { memo, useState, Children } from 'react';
import styles from "./event.module.css";
import common_types from '../../../common_files/common_types';
import { ReactComponent as InfoIcon } from "../../../assets/icons/actions/info.svg";
import { ReactComponent as CallIcon } from "../../../assets/icons/actions/callBtn.svg";
import { ReactComponent as SoftResetIcon } from "../../../assets/icons/actions/softReset.svg";
import { ReactComponent as HardResetIcon } from "../../../assets/icons/actions/hardReset.svg";
import { ReactComponent as VideoCameraIcon } from "../../../assets/icons/actions/video-camera.svg";
import { ReactComponent as FocusIcon } from "../../../assets/icons/actions/centerLoc.svg";
import { ReactComponent as HighlightPathIcon } from "../../../assets/icons/actions/pathloc.svg";
import { ReactComponent as MicIcon } from "../../../assets/icons/actions/microphone.svg";
import { ReactComponent as ResolveIcon } from "../../../assets/icons/actions/resolved.svg";
import { ReactComponent as AssistCartIcon } from "../../../assets/icons/event/buttons/assist.svg";
import DraggableCartPanel from './DraggableCartPanel';
import useAuth from "../../../hooks/Auth/useAuth";
import softResetCart from '../../../services/softResetCart';
import hardResetCart from '../../../services/hardResetCart';
import Swal from "sweetalert2";
import AnimatedChip from '../../AnimatedChip/AnimatedChip';
import CustomChip from '../../AnimatedChip/CustomChip';

const EVENT_TYPES = common_types.event_type_enum;
const OPERATION_OPTIONS = common_types.operation_options_list;
const CART_EVENT_OPERATION_OPTIONS = common_types.operation_cart_event_list;

// Button components map for standard operations
const ButtonFactoryMap = {
  [OPERATION_OPTIONS.OPERATION_SOFT_RESET]: ({ onClick }) => (
    <AnimatedChip 
      icon={<SoftResetIcon />} 
      text="SoftReset" 
      onAction={onClick} 
    />
  ),
  [OPERATION_OPTIONS.OPERATION_HARD_RESET]: ({ onClick }) => (
    <AnimatedChip 
      icon={<HardResetIcon />} 
      text="HardReset" 
      onAction={onClick} 
    />
  ),
  [OPERATION_OPTIONS.OPERATION_OPEN_CAMERAS]: ({ onClick }) => (
    <AnimatedChip 
      icon={<VideoCameraIcon />} 
      text="Cameras" 
      onAction={onClick} 
    />
  ),
  [OPERATION_OPTIONS.OPERATION_CALL_USER]: ({ onClick }) => (
    <AnimatedChip 
      icon={<CallIcon />} 
      text="Call" 
      onAction={onClick} 
    />
  ),
};

function BottomActions({
  event_type,
  eventSubtype,
  eventId,
  resolveEvent,
  cartId,
  focusOnCart,
  callBack,
  userId,
  eventName,
  isCartEvent,

  operationOptionsList = [],
  openCamerasModal,
  openRecorderModal
}) {
  let { cartsPaths, cartToShowPath, setCartToShowPath, relevantUrl, selectedCart, isPhoneSize } = useAuth();
  const [showCartPanel, setShowCartPanel] = useState(false);

  const cartEventOps = operationOptionsList.filter(op =>
    Object.values(CART_EVENT_OPERATION_OPTIONS).includes(op)
  );
    // 2) If cart ops exist, drop the Cameras button from operations
    const filteredOps = cartEventOps.length > 0
    ? operationOptionsList.filter(op => op !== OPERATION_OPTIONS.OPERATION_OPEN_CAMERAS)
    : operationOptionsList;

  const handleClick = (operation) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    switch (operation) {
      case OPERATION_OPTIONS.OPERATION_SOFT_RESET:
        triggerSoftReset(e);
        break;
      case OPERATION_OPTIONS.OPERATION_HARD_RESET:
        triggerHardReset(e);
        break;
      case OPERATION_OPTIONS.OPERATION_READ_TIP:
        break;
      case OPERATION_OPTIONS.OPERATION_OPEN_CAMERAS:
        openCamerasModal(cartId);
        break;
      case OPERATION_OPTIONS.OPERATION_CALL_USER:
        callBack(userId);
        break;
      default:
        break;
    }
  };

  const triggerHighlightPath = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (cartToShowPath.includes(cartId)) {
      setCartToShowPath(cartToShowPath.filter(item => item !== cartId));
    } else {
      setCartToShowPath([...cartToShowPath, cartId]);
    }
  };

  const triggerFocusOnCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    focusOnCart(cartId);
  };

  const triggerOpenRecorder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openRecorderModal(cartId);
  };

  const triggerSoftReset = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const result = await Swal.fire({
      title: "Confirm Soft Reset",
      text: `Are you sure you want to perform a soft reset on cart ${cartId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      const resetResult = await softResetCart(cartId, relevantUrl);
      if (resetResult === 'error') {
        console.error("Error during soft reset");
      } else {
        console.log("Soft reset successful");
      }
    }
  };

  const triggerHardReset = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const result = await Swal.fire({
      title: "Confirm Hard Reset",
      text: `Are you sure you want to perform a hard reset on cart ${cartId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      const resetResult = await hardResetCart(cartId, relevantUrl);
      if (resetResult === 'error') {
        console.error("Error during hard reset");
      } else {
        console.log("Hard reset successful");
      }
    }
  };

  const renderOperationButtons = () =>
    filteredOps.map((operation, index) => {
      const ButtonComponent = ButtonFactoryMap[operation];
      if (!ButtonComponent) return null;
      return <ButtonComponent key={`op-${index}`} onClick={handleClick(operation)} />;
    });

// Updated: Return an explicit array of chips and add dynamic styles based on focus/highlight.
const renderDefaultButtons = (selectedCartOverride, cartToShowPathOverride) => {
  const chips = [];
  // Use live overrides when provided (e.g., from phone slider), else fall back to local state
  const selectedCartLive = (selectedCartOverride !== undefined) ? selectedCartOverride : selectedCart;
  const cartToShowPathLive = Array.isArray(cartToShowPathOverride) ? cartToShowPathOverride : cartToShowPath;
  if (isCartEvent) {
    // If selectedCart matches current cartId, add a blue background.
    const focusStyle = selectedCartLive === cartId ? {  backgroundColor: 'var(--carteav)',color:'var(--white)' } : {};
    // If current cartId is in cartToShowPath, add a blue background.
    const highlightStyle = cartToShowPathLive.includes(cartId) ? { backgroundColor: 'var(--carteav)',color:'var(--white)' } : {};

    chips.push(
      <AnimatedChip
        key="focus"
        icon={<FocusIcon />}
        text="Focus"
        onAction={triggerFocusOnCart}
        style={focusStyle}
      />
    );
    chips.push(
      <AnimatedChip
        key="highlight"
        icon={<HighlightPathIcon />}
        text="Highlight"
        onAction={triggerHighlightPath}
        style={highlightStyle}
      />
    );
    chips.push(
      <AnimatedChip
        key="recorder"
        icon={<MicIcon />}
        text="Recorder"
        onAction={triggerOpenRecorder}
      />
    );
  } 
  if (!isCartEvent || eventSubtype === common_types.TYPE_OPERATOR_TREATMENT.TEMPORARY_EVENT) { 
      chips.push(
        <AnimatedChip
          key="resolve"
          icon={<ResolveIcon />}
          text="Resolve"
          onAction={(e) => {
            e.stopPropagation();
            e.preventDefault();
            resolveEvent(eventId);
          }}
        />
      );
  }
  return chips;
};

  // Build an array combining both operation and default buttons
  const renderButtonsRows = (selectedCartOverride, cartToShowPathOverride) => {
    // Convert children arrays
    const opButtons = filteredOps.length > 0
      ? Children.toArray(renderOperationButtons())
      : [];
  const defButtons = Children.toArray(renderDefaultButtons(selectedCartOverride, cartToShowPathOverride));
    const allButtons = [...opButtons, ...defButtons];

    // If 4 or fewer buttons, render one row.
    if (allButtons.length <= 4) {
      return <div className={styles.buttonRow}>{allButtons}</div>;
    }

    // Otherwise, split into 2 rows. 
    // First row gets Math.ceil(total/2) and second gets the rest.
    const half = Math.ceil(allButtons.length / 2);
    const firstRow = allButtons.slice(0, half);
    const secondRow = allButtons.slice(half);

    return (
      <div className={styles.buttonsContainer}>
        <div className={styles.buttonRow}>{firstRow}</div>
        <div className={styles.buttonRow}>{secondRow}</div>
      </div>
    );
  };

  const renderAssistCartChip = () => (
    <div
      style={{
        width: "100%",
        marginTop: "8px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <CustomChip
        icon={null}
        label="Assist Cart"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isPhoneSize) {
            // On phone, open the bottom slider with Assist Cart panel
            window.dispatchEvent(
              new CustomEvent('openAssistCartPanel', {
                detail: {
                  cartId,
                  eventType: event_type,
                  eventSubtype,
                  eventName,
                  cartEventOps,
                  // Pass the bottom actions renderer so the slider can display the same actions
                  renderButtonsRows,
                  // Pass live state needed for dynamic styles in the slider
                  selectedCart,
                  cartToShowPath
                }
              })
            );
          } else {
            // Desktop: open draggable panel inline
            setShowCartPanel(true);
          }
        }}
        style={{
          width: '80%',
          justifyContent: 'center',
          backgroundColor: showCartPanel ? "var(--carteav)" : "#e0e0e0",
          color: showCartPanel ? "var(--white)" : "#333",
        }}
      />
    </div>
  );



  return (
    <>
      <div className={styles.buttonRowContainer}>
        {renderButtonsRows()}
      </div>
      {cartEventOps.length > 0 && renderAssistCartChip()}
      {showCartPanel && (
        <DraggableCartPanel
          cartId={cartId}
          cartEventOps={cartEventOps}
          onClose={() => setShowCartPanel(false)}
          eventType={event_type}
          eventSubtype={eventSubtype}
          eventName={eventName}
          parameters=""
          renderButtonsRows={renderButtonsRows}
        />
      )}
    </>
  );
}

export default memo(BottomActions);
